/**
 * ProjectActivityChart
 *
 * The last day of a project's activity, hour by hour: what we requested (blue
 * area) and what actually changed (thin green bars).
 *
 * **Why two axes.** A feed is polled dozens of times a day and changes a handful of
 * times a month; on one scale the change series would be flat against the floor, and
 * the chart would answer "did anything change?" with "looks like nothing, ever".
 * Two scales are the honest way to show both series, and they come with a duty the
 * copy states rather than hides: on two axes the *slopes* are not comparable, so
 * the legend names the axis of each series and the two are drawn in different forms
 * — a filled area for requests, thin bars for changes — instead of two similar
 * shapes that invite a comparison that would be meaningless.
 *
 * **Why bars for changes and not a line.** The change series is mostly zero with
 * occasional single changes. A line through those points draws a rising or falling
 * edge between neighbouring hours, which is a claim the data does not make: one
 * hour's change says nothing about the next. A bar per hour states what happened in
 * that hour and nothing else.
 *
 * **Why hour instants, not "hour of day".** The server sends absolute hour starts,
 * so the labels are rendered in the reader's own timezone (ui/instant.js) and there
 * is no aggregated-day phase to rotate, unlike the feed screen's 28-day profile.
 *
 * **Why the failure badge is here.** "Requests: 8 640" answers how much we asked
 * for and says nothing about whether any of it worked; a request count is only
 * reassuring next to the share of it that came back broken. The failed count comes
 * from the same hourly counter as the requests, so the two are read in one window
 * and cannot drift, and the badge's colour carries the ratio so the reader does
 * not have to divide two numbers to see that something is wrong.
 *
 * Props:
 *   data — the mapped `GET /projects/{id}/activity` response
 */

import { failureRate, failureTone, formatFailureShare } from "../ui/failureTone.js";
import { axisTicks } from "../ui/chartAxis.js";
import { accountZone, parseInstant } from "../ui/instant.js";

/**
 * The chart is drawn in a fixed virtual coordinate space and scaled to whatever
 * width it is given, so it fills its card instead of sitting in a corner of it.
 *
 * Both halves of that matter. The viewBox is *wide* (the panel's content column is
 * about 1080px), because a viewBox taller than the space it is scaled into becomes
 * the limiting dimension: with a 336×120 box and a fixed 120px height the drawing
 * could never grow past 336px however wide the card was. And the height is derived
 * from the viewBox rather than set, so the aspect ratio — not a pixel height —
 * decides how tall the chart ends up.
 *
 * Text is inside the scaled space, so it grows and shrinks with the chart. The
 * sizes below are chosen for the full-width case (roughly 1:1) and stay readable
 * down to a half-width window, which is the range this panel is used at.
 */
const VIEW = { width: 1080, height: 168 };
const PADDING = { top: 10, right: 46, bottom: 22, left: 46 };

/** hourLabel renders a bucket's local wall clock, on the hour. */
function hourLabel(value) {
  const at = parseInstant(value);
  if (!at) return "—";
  return at.toLocaleTimeString(undefined, { timeZone: accountZone(), hour: "2-digit", minute: "2-digit" });
}

function dayLabel(value) {
  const at = parseInstant(value);
  if (!at) return "";
  return at.toLocaleDateString(undefined, { timeZone: accountZone(), day: "numeric", month: "short" });
}

/**
 * axisLabelIndices picks five evenly spaced buckets to label: enough to read the
 * axis across a full-width chart, few enough that the labels never collide at the
 * narrow end of the range. Duplicates are dropped, so a short window is labelled
 * with as many positions as it has.
 */
function axisLabelIndices(count) {
  const positions = 4;
  const indices = [];
  for (let i = 0; i <= positions; i++) {
    const index = Math.round((i * (count - 1)) / positions);
    if (!indices.includes(index)) indices.push(index);
  }
  return indices;
}

export default function ProjectActivityChart({ data }) {
  const buckets = data?.buckets ?? [];

  if (buckets.length === 0) {
    return (
      <div className="ical-card">
        <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-base)" }}>Activity</p>
        <p style={{ margin: "6px 0 0", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          No activity has been recorded for this project yet. The chart fills in as the worker polls
          its calendars.
        </p>
      </div>
    );
  }

  const maxRequests = Math.max(...buckets.map((bucket) => bucket.requests), 0);
  const maxChanges = Math.max(...buckets.map((bucket) => bucket.changes), 0);
  const requestTicks = axisTicks(maxRequests);
  const changeTicks = axisTicks(maxChanges);
  const requestTop = requestTicks[requestTicks.length - 1] || 1;
  const changeTop = changeTicks[changeTicks.length - 1] || 1;

  const width = VIEW.width;
  const plotWidth = width - PADDING.left - PADDING.right;
  const plotHeight = VIEW.height - PADDING.top - PADDING.bottom;
  const step = plotWidth / buckets.length;

  // Requests as a filled area: the shape of traffic is what matters, and a fill
  // reads as "volume" rather than as a value to compare with the green line.
  const areaPoints = buckets.map((bucket, index) => {
    const x = PADDING.left + step * (index + 0.5);
    const y = PADDING.top + plotHeight * (1 - bucket.requests / requestTop);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const areaPath = `M${PADDING.left},${PADDING.top + plotHeight} L${areaPoints.join(" L")} L${(
    PADDING.left + plotWidth
  ).toFixed(1)},${PADDING.top + plotHeight} Z`;

  // Changes are drawn as thin bars rather than a line: the series is mostly zero
  // with occasional single changes, and a line through that reads as a trend
  // between neighbouring hours, which there is none of — one hour's change says
  // nothing about the next. A bar per hour says exactly what happened, and where
  // nothing did.
  //
  // The width is a fraction of the band with a floor and a ceiling: thin enough to
  // stay "a bar per hour" at 24 buckets across a full-width card, and never so wide
  // that a week-long window (168 bands) turns into a solid block.
  const barWidth = Math.max(1.4, Math.min(6, step * 0.18));
  const changeBars = buckets.map((bucket, index) => ({
    bucket,
    x: PADDING.left + step * (index + 0.5) - barWidth / 2,
    y: PADDING.top + plotHeight * (1 - bucket.changes / changeTop),
    width: barWidth,
    height: plotHeight * (bucket.changes / changeTop),
  }));

  const last = buckets[buckets.length - 1];

  const requests = data.totals?.requests ?? 0;
  const failed = data.totals?.failed ?? 0;
  const failureShare = failureRate(failed, requests);
  const failureColor = failureTone(failureShare);

  return (
    <div className="ical-card">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-base)" }}>
          Requests and changes · last {data.hours} h
        </p>
        <span style={{ display: "flex", gap: 14, fontSize: "var(--text-xs)", alignItems: "center" }}>
          {/* The legend names the axis, because that is the whole caveat of a
              two-axis chart: the heights are not comparable. */}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 2, background: "var(--activity-requests, #7fb3e8)" }} />
            Requests (left axis): {requests.toLocaleString()}
          </span>
          {/* Beside the request total, because the two are read together: the
              count is what we asked for and this is how much of it did not work.
              The colour is the ratio, so "is this bad?" needs no arithmetic. */}
          <span
            data-failed={failed}
            data-failure-share={failureShare}
            title={`${failed.toLocaleString()} of the ${requests.toLocaleString()} requests in this window failed (${formatFailureShare(
              failureShare,
            )}). The colour runs from grey to burgundy over 0–10% and stays burgundy above it.`}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, color: failureColor, fontWeight: 500 }}
          >
            <span
              aria-hidden="true"
              style={{ width: 10, height: 10, borderRadius: 2, background: failureColor, opacity: 0.85 }}
            />
            Failed: {failed.toLocaleString()} ({formatFailureShare(failureShare)})
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span aria-hidden="true" style={{ width: 4, height: 10, borderRadius: 1, background: "var(--activity-changes, #3f9c62)" }} />
            Changes (right axis): {data.totals?.changes?.toLocaleString?.() ?? data.totals?.changes ?? 0}
          </span>
        </span>
      </div>

      <p style={{ margin: "6px 0 12px", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
        Times are in your local timezone. The series are drawn against two axes because the volumes
        differ by two orders of magnitude — a day of polls per feed against a handful of changes a
        month — so their heights are not comparable with each other; the totals above are. The failed
        count is coloured on a scale from grey to burgundy across 0–10% of the requests it is counted
        from, and stays burgundy above that.
        {maxChanges === 0
          ? " Nothing changed in this window: no green bar is drawn, which is a result and not a missing series."
          : ""}
      </p>

      <svg
        viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
        role="img"
        aria-label={`Requests and changes per hour for the last ${data.hours} hours`}
        // No width/height attributes: the style decides, and `height: auto` is what
        // makes the viewBox's aspect ratio set the rendered height.
        style={{ display: "block", width: "100%", height: "auto", overflow: "visible" }}
      >
        {/* Grid at the request axis' ticks, so a bar can be read off a line. */}
        {requestTicks.map((tick) => {
          const y = PADDING.top + plotHeight * (1 - tick / requestTop);
          return (
            <g key={`grid-${tick}`}>
              <line
                x1={PADDING.left}
                x2={PADDING.left + plotWidth}
                y1={y}
                y2={y}
                stroke="var(--border)"
                strokeWidth="1"
                strokeDasharray={tick === 0 ? undefined : "2 3"}
              />
              <text x={PADDING.left - 8} y={y + 4} textAnchor="end" fontSize="12" fill="var(--text-muted)">
                {tick}
              </text>
            </g>
          );
        })}

        {/* The change axis on the right, in the green the line uses. */}
        {changeTicks.map((tick) => {
          const y = PADDING.top + plotHeight * (1 - tick / changeTop);
          return (
            <text key={`changes-${tick}`} x={PADDING.left + plotWidth + 8} y={y + 4} fontSize="12" fill="var(--activity-changes, #3f9c62)">
              {tick}
            </text>
          );
        })}

        <path d={areaPath} fill="var(--activity-requests, #7fb3e8)" fillOpacity="0.35" stroke="var(--activity-requests, #7fb3e8)" strokeWidth="1" />

        {/* Drawn after the area, so a change is never hidden behind traffic. */}
        {changeBars.map((bar) =>
          bar.bucket.changes > 0 ? (
            <rect
              key={bar.bucket.at}
              x={bar.x}
              y={bar.y}
              width={bar.width}
              height={bar.height}
              rx={Math.min(1, bar.width / 3)}
              fill="var(--activity-changes, #3f9c62)"
              data-changes={bar.bucket.changes}
            >
              <title>{`${bar.bucket.changes} change${bar.bucket.changes === 1 ? "" : "s"} at ${hourLabel(bar.bucket.at)}`}</title>
            </rect>
          ) : null,
        )}

        {/* The hour in progress is marked, so a short last bar is not read as a drop. */}
        {last?.partial ? (
          <rect
            x={PADDING.left + plotWidth - step}
            y={PADDING.top}
            width={step}
            height={plotHeight}
            fill="var(--text-muted)"
            fillOpacity="0.08"
          >
            <title>{`The current hour, still filling (${hourLabel(last.at)}).`}</title>
          </rect>
        ) : null}

        {/* Five labels at even positions: one per hour is unreadable, and the ends
            alone leave the middle of a full-width axis unlabelled. */}
        {axisLabelIndices(buckets.length).map((index, position, all) => {
          const bucket = buckets[index];
          const anchor = position === 0 ? "start" : position === all.length - 1 ? "end" : "middle";
          const x = PADDING.left + step * (index + 0.5);
          const isLast = index === buckets.length - 1;
          return (
            <text
              key={`label-${index}`}
              x={x}
              y={VIEW.height - 6}
              textAnchor={anchor}
              fontSize="12"
              fill="var(--text-muted)"
            >
              {isLast && bucket?.partial ? "now" : `${dayLabel(bucket?.at)} ${hourLabel(bucket?.at)}`}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
